import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InteractionStatus } from '@azure/msal-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import moment, { Moment } from 'moment';
import { BehaviorSubject, EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { IApiToken, IAuthenticationService, IUserInfo } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IAuthenticationStorage } from '@nimbus-library/features/authentication/services/i-authentication-storage';
import { RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { IErrorStatus } from '@nimbus-library/features/shared/models/interfaces';
import { IWallboardAccessResponse, IWallboardSession } from '../../models/wallboard-authentication.model';
import { WallboardConst } from '../../models/wallboard.const';
import { isTransientError, retryTransientErrorsOperator } from '../../operators/retry-transient-errors.operator';
import { WallboardActions } from '../../store/wallboard.actions';
import { WallboardSelectors } from '../../store/wallboard.selectors';
import { WallboardAccessApiClient } from '../api/wallboard-access.api-client';

const WALLBOARD_API_URL_SEGMENT = '/api/v2/wallboards/';
const WALLBOARD_USER_ID = 'wallboard';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WallboardAuthenticationService extends IAuthenticationService {
  private readonly session$: BehaviorSubject<IWallboardSession> = new BehaviorSubject<IWallboardSession>(null);
  private readonly isWallboardNotFoundSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public isAuthenticated = false;
  public status$: Observable<InteractionStatus> = of(InteractionStatus.None);
  public readonly isWallboardNotFound$: Observable<boolean> = this.isWallboardNotFoundSubject$.asObservable();

  public constructor(
    private readonly store: Store,
    protected readonly recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    protected readonly authenticationStorage: IAuthenticationStorage,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly wallboardAccessApiClient: WallboardAccessApiClient,
  ) {
    super(recurringTaskRunnerFactory, authenticationStorage);
    this.startRenewalScheduler();
  }

  public get isWallboardNotFound(): boolean {
    return this.isWallboardNotFoundSubject$.value;
  }

  public login(token?: string): Observable<void> {
    if (!token) {
      return throwError(() => new Error('Wallboard token is missing.'));
    }

    return this.wallboardAccessApiClient.access(token).pipe(
      tap((response) => this.startSession(response)),
      map((): void => undefined),
    );
  }

  public resume(): Observable<void> {
    return this.wallboardAccessApiClient.renew().pipe(
      tap((response) => this.startSession(response)),
      map((): void => undefined),
    );
  }

  public getDashboardId(): string {
    return this.session$.value?.dashboardId;
  }

  public getUserInfo(): IUserInfo {
    return {
      userId: WALLBOARD_USER_ID,
      tenantId: this.store.selectSnapshot(WallboardSelectors.tenantId) ?? '',
      userName: WALLBOARD_USER_ID,
      name: 'Wallboard',
    };
  }

  public trackAuthentication(): Observable<void> {
    return of(undefined);
  }

  public getApiToken$(): Observable<IApiToken> {
    return of({ token: undefined, resource: undefined, permissions: [] });
  }

  public getRawApiToken$(): Observable<unknown> {
    return of(undefined);
  }

  public grantExplicitConsent$(): Observable<never> {
    return throwError(() => new Error('Not supported for the wallboard.'));
  }

  public sso(): Observable<boolean> {
    return of(false);
  }

  public logout(): void {}

  public override onUnauthorized(request: HttpRequest<unknown>): void {
    if (request.url.includes(WALLBOARD_API_URL_SEGMENT)) {
      this.setWallboardNotFound();
    }
  }

  public aquireIdToken$(): Observable<{ token: string; expiration: Moment }> {
    return throwError(() => new Error('Not supported for the wallboard.'));
  }

  public override renewToken(): Observable<{ token: string; expiration: Moment }> {
    return of(undefined);
  }

  public endSession(): void {
    this.setWallboardNotFound();
    this.wallboardAccessApiClient
      .end()
      .pipe(
        retryTransientErrorsOperator(WallboardConst.transientRetry.maxRetries),
        catchError(() => EMPTY),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public setWallboardNotFound(): void {
    this.session$.next(null);
    if (!this.isWallboardNotFound) {
      this.isWallboardNotFoundSubject$.next(true);
    }
  }

  private startSession(response: IWallboardAccessResponse): void {
    this.session$.next({
      dashboardId: response.dashboardId,
      expiresAt: moment(response.expiresAt),
      renewAfter: moment(response.renewAfter),
    });
    this.isAuthenticated = true;
    this.isAuthenticated$.next(true);
  }

  private startRenewalScheduler(): void {
    this.session$
      .pipe(
        switchMap((session) => {
          if (!session) {
            return EMPTY;
          }

          return this.store.select(WallboardSelectors.bootstrap).pipe(
            filter((bootstrap) => !!bootstrap),
            take(1),
            switchMap(() => this.backgroundTaskFactory.getServerWatches().pipe(take(1))),
            switchMap((serverNow) => {
              const renewDelayMs = Math.max(session.renewAfter.diff(serverNow), WallboardConst.renew.minDelayMs);
              return timer(renewDelayMs);
            }),
            switchMap(() =>
              this.wallboardAccessApiClient.renew().pipe(
                retry({
                  delay: (error: IErrorStatus) =>
                    isTransientError(error)
                      ? this.backgroundTaskFactory.getServerWatches().pipe(
                          take(1),
                          switchMap((now) =>
                            now.clone().add(WallboardConst.renew.retryDelayMs).isBefore(session.expiresAt)
                              ? timer(WallboardConst.renew.retryDelayMs)
                              : throwError(() => error),
                          ),
                        )
                      : throwError(() => error),
                }),
                catchError(() => {
                  this.setWallboardNotFound();
                  return EMPTY;
                }),
              ),
            ),
            tap(() => this.store.dispatch(new WallboardActions.Refresh())),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe((response) => this.startSession(response));
  }
}
